// src/pages/ProtocolsPage.jsx
import React, { useEffect, useMemo, useState } from "react";
import * as Lucide from "lucide-react";
import { motion } from "framer-motion";
import {
  getProtocolsConfig,
  getProtocolsContent,
  listProtocolDocs, // usamos la lista para filtrar visibles
  downloadProtocolDocUrl,
} from "../../../services/protocolsService";
import { Banner } from "../../../components/common/Banner";

/* ---- helpers ---- */
const IconPreview = ({ name, size = 22, className }) => {
  const Cmp = (name && Lucide[name]) || Lucide.FileText;
  return <Cmp size={size} className={className || "text-[#0B4A8F]"} />;
};
const isAbsUrl = (u) => /^https?:\/\//i.test(String(u || ""));
const absoluteUrl = (u) => (isAbsUrl(u) ? u : String(u || ""));

const card = "rounded-2xl border bg-white/90 backdrop-blur ring-1 ring-black/5";
const inputBtn = "inline-flex items-center gap-2 px-4 py-2 rounded-xl";
const btnPri = `${inputBtn} bg-[#0488AF] text-white hover:bg-[#037a98]`;

/* Badges tipo CrimePrevention */
const Pill = ({ children }) => (
  <span className="inline-flex items-center gap-2 rounded-full bg-blue-50 text-[#0B4A8F] border border-blue-100 px-3 py-1 text-sm font-medium">
    {children}
  </span>
);
const Dot = () => (
  <span className="inline-block w-1.5 h-1.5 rounded-full bg-gray-300" />
);

/* Miniatura con fallback: intenta coverUrl; si no, embebe la 1ª página del PDF */
function CoverPreview({ coverSrc, pdfHref, alt = "Portada", size = "xs" }) {
  const [imgOk, setImgOk] = useState(Boolean(coverSrc));
  const sizeCls =
    size === "xs"
      ? "w-[80px] h-[112px] sm:w-[100px] sm:h-[140px]"
      : size === "sm"
      ? "w-[120px] h-[160px] sm:w-[140px] sm:h-[190px] md:w-[160px] md:h-[220px]"
      : size === "md"
      ? "w-[140px] h-[190px] sm:w-[160px] sm:h-[220px] md:w-[180px] md:h-[250px]"
      : "w-[180px] h-[250px] sm:w-[200px] sm:h-[280px] md:w-[220px] md:h-[310px]"; // lg

  const canEmbed = Boolean(pdfHref);

  return (
    <div
      className={`${sizeCls} rounded-xl border border-blue-100 bg-white overflow-hidden flex items-center justify-center shadow-sm`}
    >
      {imgOk && coverSrc ? (
        <img
          src={absoluteUrl(coverSrc)}
          alt={alt}
          className="w-full h-full object-cover"
          onError={() => setImgOk(false)}
          loading="lazy"
        />
      ) : canEmbed ? (
        <embed
          src={`${absoluteUrl(pdfHref)}#page=1&zoom=105`}
          type="application/pdf"
          className="w-full h-full block"
        />
      ) : null}
    </div>
  );
}

/* ======================================================= */
export default function ProtocolsPage() {
  const [loading, setLoading] = useState(true);
  const [cfg, setCfg] = useState({
    pageTitle: "Protocolos",
    subtitle: "",
    lastReview: "",
  });

  // Guardamos tanto sections como protocols para compatibilidad
  const [content, setContent] = useState({
    lastReview: "",
    sections: [],
    protocols: [],
  });

  const [latestDoc, setLatestDoc] = useState(null); // solo documentos visibles

  useEffect(() => {
    (async () => {
      try {
        const [c, cnt, docs] = await Promise.all([
          getProtocolsConfig().catch(() => ({
            pageTitle: "Protocolos",
            subtitle: "",
            lastReview: "",
          })),
          getProtocolsContent().catch(() => ({
            lastReview: "",
            sections: [],
            protocols: [],
          })),
          listProtocolDocs().catch(() => []),
        ]);

        const cfgNext = {
          pageTitle: c?.pageTitle || "Protocolos",
          subtitle: c?.subtitle || "",
          // si config no trae lastReview, usamos el de content
          lastReview: c?.lastReview ?? cnt?.lastReview ?? "",
        };

        setCfg(cfgNext);

        const cleanContent = {
          lastReview: cnt?.lastReview || "",
          sections: Array.isArray(cnt?.sections) ? cnt.sections : [],
          protocols: Array.isArray(cnt?.protocols) ? cnt.protocols : [],
        };

        setContent(cleanContent);

        // filtra solo visibles y toma el más reciente
        const visibleDocs = Array.isArray(docs)
          ? docs.filter((d) => !!d.isPublished)
          : [];
        const latestVisible =
          visibleDocs
            .slice()
            .sort(
              (a, b) =>
                +new Date(b.createdAt || 0) - +new Date(a.createdAt || 0)
            )[0] || null;

        setLatestDoc(latestVisible);
      } finally {
        setLoading(false);
      }
    })();
  }, []);

  // Fuente principal: sections, fallback: protocols
  const protos = useMemo(() => {
    if (Array.isArray(content?.sections) && content.sections.length) {
      return content.sections;
    }
    if (Array.isArray(content?.protocols)) {
      return content.protocols;
    }
    return [];
  }, [content]);

  const lastReview = cfg?.lastReview || content?.lastReview || "";

  if (loading) {
    return (
      <div className="max-w-6xl mx-auto p-6">
        <div className="animate-pulse space-y-4">
          <div className="h-10 bg-gray-100 rounded w-1/3" />
          <div className="h-5 bg-gray-100 rounded w-2/3" />
          <div className="h-24 bg-gray-100 rounded" />
        </div>
      </div>
    );
  }

  const pdfHref = latestDoc
    ? latestDoc.secureUrl
      ? absoluteUrl(latestDoc.secureUrl)
      : downloadProtocolDocUrl(latestDoc.id)
    : null;
  const coverUrl = latestDoc?.coverUrl
    ? absoluteUrl(latestDoc.coverUrl)
    : null;

  return (
    <div className="max-w-screen-xl mx-auto px-4 py-8">
      <Banner
        title={cfg?.pageTitle || "Protocolos"}
        message={cfg?.subtitle || "Guías y procedimientos oficiales"}
        className="mb-6"
      />

      <div className="space-y-6">
        {/* Badges de contexto (igual que CrimePrevention) */}
        <div className="flex flex-wrap items-center gap-2 text-sm">
          {lastReview && <Pill>Última revisión: {lastReview}</Pill>}
          {latestDoc?.versionTag && (
            <>
              <Dot />
              <Pill>Versión PDF: {latestDoc.versionTag}</Pill>
            </>
          )}
        </div>

        {/* HERO (solo si hay documento visible) */}
        {latestDoc && (
          <div className="rounded-3xl bg-gradient-to-t from-[#0EC5DF] via-[#0488AF] to-[#0B4A8F] text-white px-5 py-6 md:px-7 md:py-7 mb-10 shadow-sm">
            <div className="grid gap-5 md:gap-7 md:grid-cols-[minmax(240px,280px),minmax(0,1fr)] items-start">
              {/* Portada */}
              <div className="w-full max-w-[280px] justify-self-center md:justify-self-start">
                <div className="aspect-[3/4] rounded-2xl bg-white overflow-hidden shadow-md">
                  {coverUrl ? (
                    <img
                      className="w-full h-full object-cover"
                      src={coverUrl}
                      alt="Portada Protocolos"
                      onError={(e) => {
                        if (pdfHref) {
                          e.currentTarget.parentElement.innerHTML = `<embed type="application/pdf" src="${pdfHref}#page=1&zoom=125" style="width:100%;height:100%;display:block;border:0" />`;
                        }
                      }}
                    />
                  ) : pdfHref ? (
                    <embed
                      type="application/pdf"
                      src={`${pdfHref}#page=1&zoom=125`}
                      style={{ width: '100%', height: '100%', display: 'block', border: 0 }}
                    />
                  ) : null}
                </div>
              </div>

              {/* Título + botón */}
              <div className="w-full min-w-0">
                <div className="text-xl md:text-2xl font-semibold mb-3 text-white">
                  {latestDoc?.title?.trim() ||
                    "Documento oficial de Protocolos"}
                </div>
                {pdfHref && (
                  <a
                    href={pdfHref}
                    target="_blank"
                    rel="noreferrer"
                    className="inline-flex items-center gap-2 px-4 py-2 rounded-xl bg-white/95 text-[#0488AF] hover:bg-white font-medium shadow-sm"
                  >
                    <Lucide.FileDown size={18} />
                    Ver / Descargar Protocolo
                  </a>
                )}
              </div>
            </div>
          </div>
        )}

        {/* Lista de protocolos / secciones */}
        {protos.length === 0 ? (
          <div className="text-gray-500">
            Aún no hay secciones publicadas.
          </div>
        ) : (
          <div className="grid gap-5 md:grid-cols-2">
            {protos.map((p, i) => {
              // Soporte tanto body/bullets como summary/steps
              const summary =
                Array.isArray(p?.body) && p.body.length
                  ? p.body
                  : Array.isArray(p?.summary)
                  ? p.summary
                  : [];
              const steps =
                Array.isArray(p?.bullets) && p.bullets.length
                  ? p.bullets
                  : Array.isArray(p?.steps)
                  ? p.steps
                  : [];

              // CTA: soportar múltiples botones (contactUrl, policyUrl, reportUrl)
              const cta = p?.cta || {};
              const hasContact = !!(cta.contactUrl || (cta.url && /^mailto:/i.test(cta.url)));
              const hasPolicy = !!cta.policyUrl;
              const hasReport = !!cta.reportUrl;
              const hasAnyCta = hasContact || hasPolicy || hasReport || !!cta.url;

              if (!hasAnyCta) {
                return (
                  <motion.article
                    key={i}
                    initial={{ opacity: 0, y: 10 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.25 }}
                    className={`${card} overflow-hidden`}
                  >
                    {/* Separador de color */}
                    <div className="h-1 w-full bg-gradient-to-r from-[#0488AF] to-[#C42251]" />

                    <div className="p-5 space-y-4">
                      <div className="flex items-center gap-3">
                        {p?.iconImageUrl ? (
                          <img
                            src={p.iconImageUrl}
                            alt=""
                            className="h-7 w-7 object-contain rounded"
                          />
                        ) : (
                          <IconPreview name={p?.icon} />
                        )}
                        <h3 className="text-lg font-semibold">
                          {p?.title || "Protocolo"}
                        </h3>
                      </div>

                      {summary.length > 0 && (
                        <div className="space-y-2">
                          {summary.map((s, si) => (
                            <p key={si} className="text-gray-700">
                              {s}
                            </p>
                          ))}
                        </div>
                      )}

                      {steps.length > 0 && (
                        <div className="space-y-2">
                          <div className="text-sm font-semibold text-gray-700">
                            Pasos
                          </div>
                          <ol className="list-decimal pl-5 space-y-1">
                            {steps.map((st, sti) => (
                              <li key={sti} className="text-gray-700">
                                {st}
                              </li>
                            ))}
                          </ol>
                        </div>
                      )}
                    </div>
                  </motion.article>
                );
              }

              return (
                <motion.article
                  key={i}
                  initial={{ opacity: 0, y: 10 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.25 }}
                  className={`${card} overflow-hidden`}
                >
                  {/* Separador de color */}
                  <div className="h-1 w-full bg-gradient-to-r from-[#0488AF] to-[#C42251]" />

                  <div className="p-5 space-y-4">
                    <div className="flex items-center gap-3">
                      {p?.iconImageUrl ? (
                        <img
                          src={p.iconImageUrl}
                          alt=""
                          className="h-7 w-7 object-contain rounded"
                        />
                      ) : (
                        <IconPreview name={p?.icon} />
                      )}
                      <h3 className="text-lg font-semibold">
                        {p?.title || "Protocolo"}
                      </h3>
                    </div>

                    {/* Resumen / body */}
                    {summary.length > 0 && (
                      <div className="space-y-2">
                        {summary.map((s, si) => (
                          <p key={si} className="text-gray-700">
                            {s}
                          </p>
                        ))}
                      </div>
                    )}

                    {/* Pasos / bullets */}
                    {steps.length > 0 && (
                      <div className="space-y-2">
                        <div className="text-sm font-semibold text-gray-700">
                          Pasos
                        </div>
                        <ol className="list-decimal pl-5 space-y-1">
                          {steps.map((st, sti) => (
                            <li key={sti} className="text-gray-700">
                              {st}
                            </li>
                          ))}
                        </ol>
                      </div>
                    )}

                    {/* CTA: múltiples botones cuando hay contactUrl y policyUrl */}
                    {(hasContact || hasPolicy || hasReport || cta.url) && (
                      <div className="pt-2 flex flex-wrap gap-2">
                        {hasContact && (
                          <a
                            href={cta.contactUrl || cta.url}
                            className={btnPri}
                            target={
                              cta.contactUrl?.startsWith("/") ||
                              /^mailto:/i.test(cta.contactUrl || "")
                                ? "_self"
                                : "_blank"
                            }
                            rel="noreferrer"
                          >
                            {/^mailto:/i.test(cta.contactUrl || "") ? (
                              <Lucide.Mail size={16} />
                            ) : (
                              <Lucide.ExternalLink size={16} />
                            )}
                            {cta.label || "Contacto"}
                          </a>
                        )}
                        {hasPolicy && (
                          <a
                            href={cta.policyUrl}
                            className="inline-flex items-center gap-2 px-4 py-2 rounded-xl border bg-white hover:bg-gray-50"
                            target={
                              cta.policyUrl.startsWith("/") ? "_self" : "_blank"
                            }
                            rel="noreferrer"
                          >
                            <Lucide.ExternalLink size={16} />
                            Ver política
                          </a>
                        )}
                        {hasReport && (
                          <a
                            href={cta.reportUrl}
                            className={btnPri}
                            target={
                              cta.reportUrl.startsWith("/") ||
                              /^mailto:/i.test(cta.reportUrl)
                                ? "_self"
                                : "_blank"
                            }
                            rel="noreferrer"
                          >
                            <Lucide.Megaphone size={16} />
                            Reportar una inquietud
                          </a>
                        )}
                        {!hasContact && !hasPolicy && !hasReport && cta.url && (
                          <a
                            href={cta.url}
                            className={btnPri}
                            target={
                              cta.url.startsWith("/") ||
                              /^mailto:/i.test(cta.url)
                                ? "_self"
                                : "_blank"
                            }
                            rel="noreferrer"
                          >
                            {/^mailto:/i.test(cta.url) ? (
                              <Lucide.Mail size={16} />
                            ) : (
                              <Lucide.ExternalLink size={16} />
                            )}
                            {cta.label || "Ver más"}
                          </a>
                        )}
                      </div>
                    )}
                  </div>
                </motion.article>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
}
